import React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ImageIcon from '@mui/icons-material/Image';
import {useTranslation} from 'react-i18next';
import {GenerationDetail, GenerationListItem} from '../../models/Generation';
import {formatCreatedAt, formatDuration} from './creationFormat';

interface CreationLightboxProps {
    item: GenerationListItem | null;
    /** Loaded from GET /generations/{id}; null while it is still in flight. */
    detail: GenerationDetail | null;
    loadingDetail: boolean;
    downloading: boolean;
    retrying: boolean;
    onClose: () => void;
    onDownload: () => void;
    onRetry: () => void;
    onOpenTemplate: () => void;
    onReport: () => void;
}

const MetaRow: React.FC<{ label: string; value: React.ReactNode }> = ({label, value}) => (
    <Box sx={{
        display: 'flex', justifyContent: 'space-between', gap: 2,
        py: 0.75, borderBottom: 1, borderColor: 'divider',
    }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" sx={{textAlign: 'end'}}>{value}</Typography>
    </Box>
);

/**
 * One generation, full size, with everything a user can do about it.
 *
 * An EXPIRED row deliberately previews its thumbnail rather than the master:
 * the record is intact and the picture is permanent, but the master is past its
 * retention window, so pointing an <img> at a presigned URL for a deleted
 * object would just render a broken frame.
 */
const CreationLightbox: React.FC<CreationLightboxProps> = ({
                                                              item, detail, loadingDetail, downloading, retrying,
                                                              onClose, onDownload, onRetry, onOpenTemplate, onReport,
                                                          }) => {
    const {t} = useTranslation();
    if (!item) return null;

    const expired = detail?.expired ?? item.expired;
    const status = detail?.status ?? item.status;
    const isVideo = (detail?.outputFormat ?? item.outputFormat) === 'mp4';
    // `url` comes back null once the master is past its retention window (the
    // API checks expiry before presigning), so this falls through to the
    // permanent thumbnail on its own.
    const previewUrl = detail?.url ?? detail?.thumbnailUrl ?? item.thumbnailUrl;
    // The download does NOT use `url` — it asks /url for a fresh, logged one —
    // so it stays enabled from the list row alone, before the detail lands.
    const canDownload = status === 'completed' && !expired;

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm" data-testid="creation-lightbox">
            <DialogContent sx={{p: 2}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5}}>
                    <Typography variant="subtitle1" sx={{flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {item.templateName ?? t('main.creations.unknownTemplate')}
                    </Typography>
                    {expired && (
                        <Chip size="small" variant="outlined" label={t('main.creations.expired')}/>
                    )}
                    <Chip
                        size="small"
                        color={status === 'completed' ? 'success' : status === 'failed' ? 'error' : 'warning'}
                        label={t(`main.creations.status.${status}`)}
                    />
                    <IconButton size="small" onClick={onClose} aria-label={t('main.creations.actions.close')}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Box>

                <Box sx={{
                    backgroundColor: '#0F2233', borderRadius: 1, minHeight: 220,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1,
                }}>
                    {loadingDetail && !previewUrl ? (
                        <CircularProgress size={24}/>
                    ) : previewUrl ? (
                        isVideo && detail?.url ? (
                            <Box component="video" src={previewUrl} controls playsInline
                                 data-testid="creation-video"
                                 sx={{maxWidth: '100%', maxHeight: 420}}/>
                        ) : (
                            <Box component="img" src={previewUrl} alt={item.templateName ?? ''}
                                 sx={{maxWidth: '100%', maxHeight: 420, objectFit: 'contain'}}/>
                        )
                    ) : (
                        <ImageIcon sx={{color: 'rgba(255,255,255,0.35)', fontSize: 48}}/>
                    )}
                </Box>

                {expired && (
                    <Alert severity="info" sx={{mt: 2}}>{t('main.creations.expiredNotice')}</Alert>
                )}
                {status === 'failed' && (
                    <Alert severity="error" sx={{mt: 2}}>
                        {t('main.creations.failedNotice')}
                        {detail?.errorCode ? ` (${detail.errorCode})` : ''}
                    </Alert>
                )}
                {detail?.reportedIssue && (
                    <Alert severity="warning" sx={{mt: 2}}>{t('main.creations.report.already')}</Alert>
                )}

                <Box sx={{mt: 2}}>
                    <MetaRow label={t('main.creations.meta.createdBy')}
                             value={item.userName ?? t('main.creations.unknownUser')}/>
                    <MetaRow label={t('main.creations.meta.createdAt')} value={formatCreatedAt(item.createdAt)}/>
                    <MetaRow label={t('main.creations.meta.format')}
                             value={t(`main.creations.format.${isVideo ? 'mp4' : 'png'}`)}/>
                    <MetaRow label={t('main.creations.meta.duration')}
                             value={formatDuration(detail?.durationMs ?? item.durationMs)}/>
                </Box>

                <Box sx={{mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1}}>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon/>}
                        disabled={!canDownload || downloading}
                        onClick={onDownload}
                    >
                        {t('main.creations.actions.download')}
                    </Button>
                    {/* A WhatsApp creation has no stored inputs to retry from. */}
                    {status === 'failed' && item.actionSource !== 'whatsapp' && (
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon/>}
                            disabled={retrying}
                            onClick={onRetry}
                        >
                            {t('main.creations.actions.retry')}
                        </Button>
                    )}
                    {item.templateId && (
                        <Button startIcon={<OpenInNewIcon/>} onClick={onOpenTemplate}>
                            {t('main.creations.actions.openTemplate')}
                        </Button>
                    )}
                    <Box sx={{flex: 1}}/>
                    <Button
                        color="warning"
                        startIcon={<ReportProblemOutlinedIcon/>}
                        onClick={onReport}
                    >
                        {t('main.creations.actions.report')}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default CreationLightbox;
